import type { AxiosInstance } from 'axios';
import qs from 'query-string';
import type { WorkspaceObjects as WS } from './interfaces/workspace.object.js';
import { DownloadObjectRecursive } from './interfaces/workspace.object.js';
import { normalizeWorkspaceObject, transformDates } from './transformers/index.js';
import { normalizeWorkspace } from './transformers/workspaces.workspace.js';

export class WorkspacesObjectClient {
  constructor(private authenticatedClient: AxiosInstance) { }

  getObjectAccess = ({ objectId, workspaceId }: WS.GetObjectAccess.Request) => {
    return this.authenticatedClient.get<WS.GetObjectAccess.Response>(`/workspaces/${workspaceId}/objects/${objectId}/access`)
      .then(res => ({
        items: res.data.items,
        object: normalizeWorkspaceObject(res.data.object),
        workspace: normalizeWorkspace(res.data.workspace),
      }));
  };

  moveObject = (data: WS.MoveObject.Request) => {
    return this.authenticatedClient.post<WS.MoveObject.Response>(`/workspaces/${data.workspaceId}/objects/${data.objectId}/move`, {
      parentObjectId: data.parentObjectId,
    }).then(res => ({
      object: normalizeWorkspaceObject(res.data.object),
    }));
  };

  moveObjectDetails = (data: WS.GetMoveObjectDetails.Request) => {
    return this.authenticatedClient.get<WS.GetMoveObjectDetails.Response>(`/workspaces/${data.workspaceId}/objects/${data.objectId}/move`, {
      transformResponse: transformDates,
    }).then(res => res.data);
  };

  saveObjectAccess = (data: WS.SaveObjectAccess.Request) => {
    return this.authenticatedClient.post(`/workspaces/${data.workspaceId}/objects/${data.objectId}/access`, {
      items: data.items,
    });
  };

  updateObjectExpiration = (data: WS.UpdateObjectExpiration.Request) => {
    return this.authenticatedClient.put<WS.UpdateObjectExpiration.Response<string>>(`/workspaces/${data.workspaceId}/objects/${data.objectId}/expiration`, {
      expiresOn: data.expiresOn,
    }).then(res => ({
      object: normalizeWorkspaceObject(res.data.object),
    }));
  };

  updateObjectOwnership = (data: WS.UpdateObjectOwnership.Request) => {
    return this.authenticatedClient.post<WS.UpdateObjectOwnership.Response>(`/workspaces/${data.workspaceId}/objects/${data.objectId}/owner`, {
      ownerId: data.ownerId,
    }).then(res => ({
      object: normalizeWorkspaceObject(res.data.object),
    }));
  };

  getObjectChildren = (data: WS.GetObjectChildren.Request): Promise<WS.GetObjectChildren.Response> => {
    const query = qs.stringify({
      filters: JSON.stringify(data.filters || {}),
    }, { arrayFormat: 'bracket' });
    return this.authenticatedClient.get(`/workspaces/${data.workspaceId}/objects/${data.parentObjectId}/children?${query}`, {
      transformResponse: transformDates,
    });
  };

  getSharedObjects = (data: WS.GetSharedObjects.Request): Promise<WS.GetSharedObjects.Response> => {
    const query = qs.stringify({
      parentObjectId: data.parentObjectId,
      filters: JSON.stringify(data.filters || {}),
    }, { arrayFormat: 'bracket' });
    return this.authenticatedClient.get(`/workspaces/shared/objects?${query}`, {
      transformResponse: transformDates,
    });
  };

  downloadObjectRecursive = async ({ workspaceId, objectId, ...data }: DownloadObjectRecursive.Params & DownloadObjectRecursive.Body) => {
    const response = await this.authenticatedClient.post(`/workspaces/${workspaceId}/objects/${objectId}/download/recursive`, {
      objectIds: data.objectIds,
    });

    return DownloadObjectRecursive.responseSchema.parse(response.data);
  };

  toggleBookmark = async (data: WS.ToggleBookmark.Request): Promise<WS.ToggleBookmark.Response> => {
    return this.authenticatedClient.patch<WS.ToggleBookmark.Response>(`/workspaces/${data.workspaceId}/objects/${data.objectId}/bookmark`)
      .then(res => ({
        bookmarked: res.data.bookmarked,
      }));
  };
}

export default WorkspacesObjectClient;